import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePriceGroup } from 'core/contexts/priceGroup';
import { DeleteOutline, Edit } from '@mui/icons-material';
import { Box, Button, Fade } from '@mui/material';
import { DataGrid } from '@utriper/core-ui';

const PriceGroupGrid = ({ priceGroupId, handleOpenUnit = () => {}, handleOpenRanges = () => {}, handleOpenDeleteUnit = () => {}, handleOpenDeleteRange = () => {} }) => {
    const { t } = useTranslation();
    const translate = (key) => t(`price_group_id.price_group_grid.${key}`);
    const { startColumn, prices, setPrices, unitsPriceGroup, handleUpdatePrice } = usePriceGroup();
    const [rows, setRows] = useState([]);
    const [columns, setColumns] = useState([]);
    const [newPrice, setNewPrice] = useState(null);
    const [activeColumn, setActiveColumn] = useState(null);
    const [activeRow, setActiveRow] = useState(null);
    const canDeleteUnit = useMemo(() => unitsPriceGroup?.length > 1, [unitsPriceGroup]);
    const canDeleteRange = useMemo(() => {
        if (!Number.isInteger(activeRow?.rowId)) return false;
        if (rows?.length <= 2) return false;

        return prices.some((price) => price?.rowId !== activeRow?.rowId);
    }, [prices, rows, activeRow]);
    const highlights = useMemo(() => {
        const isColumnHighlight = Number.isInteger(activeColumn?.id);
        const isRowHighlight = Number.isInteger(activeRow?.rowId);
        const className = 'active-column';

        if (isColumnHighlight) {
            return rows?.map((row) => ({ rowId: row?.rowId, columnId: activeColumn.id, className }));
        }

        if (isRowHighlight) {
            const rowsHighlight = columns?.map((column) => ({ rowId: activeRow?.rowId, columnId: column?.columnId, className }));
            return rowsHighlight;
        }

        return [];
    }, [activeColumn, activeRow, rows, columns]);

    const getHeaderRow = () => {
        const style = { justifyContent: 'flex-start', fontSize: '14px', fontWeight: 'bold' };
        const rangeRow = { type: 'header-button', text: translate('header_row'), style, icon: <Edit fontSize="small" />, onClick: handleOpenRanges, sx: { p: 1 } };
        const unitsRows = unitsPriceGroup.map((unit) => ({ type: 'header-button', text: unit?.name, unitId: unit?.id, style, icon: <Edit fontSize="small" />, onClick: handleOpenUnit, sx: { p: 1 } }));

        const headerRow = [
            {
                rowId: 'header',
                cells: [rangeRow, ...unitsRows],
                height: 45,
            },
        ];
        return headerRow;
    };

    const getRows = (values) => {
        const style = { justifyContent: 'flex-start', px: 2, fontSize: '14px', fontWeight: 400 };
        const nonEditable = true;

        return [
            ...getHeaderRow(),
            ...values.map(({ quantityFrom, quantityTo, prices }, index) => {
                let quantityLabel = '';
                if (quantityFrom === quantityTo) {
                    quantityLabel = String(quantityFrom);
                }
                if (!Number.isInteger(quantityTo)) {
                    quantityLabel = `${quantityFrom}+`;
                }
                if (Number.isInteger(quantityTo) && quantityFrom !== quantityTo) {
                    quantityLabel = `${quantityFrom} ${translate('to')} ${quantityTo}`;
                }

                return {
                    rowId: index,
                    quantityFrom,
                    quantityTo: Number.isInteger(quantityTo) ? quantityTo : quantityFrom,
                    cells: [
                        { type: 'text', text: quantityLabel, style, nonEditable },
                        ...prices.map(({ price = null, unitId }) => ({
                            type: 'number',
                            value: !Number.isNaN(price) ? price : null,
                            unitId,
                            nonEditable: false,
                        })),
                    ],
                };
            }),
        ];
    };

    useEffect(() => {
        if (Array.isArray(unitsPriceGroup) && unitsPriceGroup.length > 0) {
            const unitColumns = [];

            // Load units columns
            for (const unit of unitsPriceGroup) {
                if (!unit?.id) continue;

                const isDuplicated = unitColumns.some((col) => col?.columnId === unit.id);
                if (!isDuplicated) {
                    unitColumns.push({ columnId: unit.id, width: 190, name: unit?.name });
                }
            }

            setColumns([startColumn, ...unitColumns]);
        }
    }, [unitsPriceGroup, startColumn]);

    useEffect(() => {
        if (Array.isArray(prices) && prices.length > 0) {
            const newRows = getRows(prices);
            setRows([...newRows]);
        } else {
            setRows([...getHeaderRow()]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prices]);

    useEffect(() => {
        if (newPrice) {
            handleUpdatePrice(priceGroupId, newPrice);
            setNewPrice(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newPrice]);

    const handleChangePrices = (changes = [], prevDetails = []) => {
        for (const { rowId, type, newCell } of changes) {
            if (type !== 'number') continue;

            const dataRow = prevDetails?.find((d) => d?.rowId === rowId);
            if (!dataRow?.prices) continue;

            const { value, unitId } = newCell;
            if (!unitId || !value) continue;

            const priceToEdit = dataRow?.prices?.find((price) => price?.unitId === unitId);
            priceToEdit.price = value;

            setNewPrice({ ...priceToEdit });
        }
    };

    const handleSelectionChange = (selection) => {
        if (!canDeleteUnit) return;
        const first = selection?.[0]?.first;
        if (!first) return;

        const { row, column } = first;

        if (column?.columnId && !Number.isInteger(column?.columnId)) {
            const lastRow = rows[rows?.length - 1];
            if (rows?.length > 2 && Number.isInteger(lastRow?.rowId) && lastRow.rowId === row?.rowId) {
                let quantityLabel = `${row.quantityFrom}`;
                if (Number.isInteger(row.quantityTo) && row.quantityFrom !== row.quantityTo) {
                    quantityLabel = `${row.quantityFrom} ${translate('to')} ${row.quantityTo}`;
                }

                const rangeRow = { rowId: row.rowId, quantityFrom: row?.quantityFrom, quantityTo: row?.quantityTo, label: quantityLabel };
                setActiveRow({ ...rangeRow });
                setActiveColumn(null);
                return;
            }
            setActiveRow(null);
            setActiveColumn(null);
            return;
        }

        if (row?.rowId === 'header') {
            const unitColumn = { id: column.columnId, name: column.name };
            setActiveColumn({ ...unitColumn });
            setActiveRow(null);
            return;
        }

        if (Number.isInteger(row?.rowId)) {
            setActiveColumn(null);
            setActiveRow(null);
            return;
        }
    };

    const handleChangeDataGrid = (changes) => {
        setPrices((prevPrices) => {
            const updatePrices = [...prevPrices];
            handleChangePrices(changes, updatePrices);

            return updatePrices;
        });
    };

    return (
        <Box component="article" sx={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: '16px' }}>
            <Box
                width="100%"
                maxWidth="1440px"
                maxHeight="768px"
                overflow="auto"
                mt={1}
                sx={{
                    '& .rg-header-button-cell': { backgroundColor: '#F2F0ED' },
                    '& .active-column': {
                        border: '1px solid #0683f9 !important',
                        backgroundColor: '#0683f940',
                    },
                }}
            >
                <DataGrid rows={rows} columns={columns} highlights={highlights} handleChange={handleChangeDataGrid} onSelectionChanged={handleSelectionChange} stickyLeftColumns={1} />
            </Box>

            {/* Delete Unit */}
            {Number.isInteger(activeColumn?.id) && canDeleteUnit && (
                <Fade in={Number.isInteger(activeColumn.id)}>
                    <Button variant="text" color="error" startIcon={<DeleteOutline />} sx={{ textTransform: 'none' }} onClick={() => handleOpenDeleteUnit(activeColumn)}>
                        {`${translate('delete')} ${activeColumn.name}`}
                    </Button>
                </Fade>
            )}

            {/* Delete Range */}
            {Number.isInteger(activeRow?.rowId) && canDeleteRange && (
                <Fade in={Number.isInteger(activeRow.rowId)}>
                    <Button variant="text" color="error" startIcon={<DeleteOutline />} sx={{ textTransform: 'none' }} onClick={() => handleOpenDeleteRange(activeRow)}>
                        {`${translate('delete')} ${activeRow.label}`}
                    </Button>
                </Fade>
            )}
        </Box>
    );
};

export default PriceGroupGrid;
